import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

const ITEMS_PER_PAGE = 5;

const SearchResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const location = useLocation();

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        const queryParams = new URLSearchParams(location.search);
        const query = queryParams.get("q");
        const pageParam = queryParams.get("page") || page;

        // Clear error when starting a new search
        setError(null);
        setLoading(true);

        if (!query) {
          setError("Please enter a valid search term.");
          setLoading(false);
          return;
        }

        if (query.length <= 2) {
          throw new Error("Your search term must be longer than 2 characters.");
        }

        const response = await fetch(`https://api.theonlysale.com/api/search?query=${query}&page=${pageParam}`);
        if (!response.ok) {
          throw new Error("Error fetching search results.");
        }

        const data = await response.json();
        setResults(data);
        setPage(parseInt(pageParam, 10)); // Set page correctly based on URL
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [location.search]); // Re-run when search query in the URL changes

  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedResults = results.slice(startIndex, endIndex);

  const totalPages = Math.ceil(results.length / ITEMS_PER_PAGE);

  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get("q");

  return (
    <>
      <Header />
      <div className="max-w-screen min-h-screen dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold mb-6 text-gray-900 dark:text-gray-100">
          Search Results
        </h1>

        {query && (
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            Showing results for: <strong>{query}</strong>
          </p>
        )}

        {loading ? (
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading...</p>
        ) : error ? (
          <div className="p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg dark:bg-red-900 dark:border-red-700 dark:text-red-300" role="alert">
            <p className="font-bold">Oops! Something went wrong:</p>
            <p>{error}</p>
          </div>
        ) : paginatedResults.length > 0 ? (
          <>
            <ul className="space-y-8">
              {paginatedResults.map((item) => {
                const parsedImages = JSON.parse(item.images);
                const imageUrl = `https://api.theonlysale.com/uploads/images/${parsedImages[0]
                  .split(/[\\/]/)
                  .pop()}`;

                return (
                  <li
                    key={item.id}
                    className="flex items-center space-x-6 p-5 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 ease-in-out hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <img
                      src={imageUrl}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                    />
                    <div className="flex-1">
                      <a
                        href={`/product/${item.id}`}
                        className="text-xl font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {item.name}
                      </a>
                      <p className="mt-1 text-lg text-gray-700 dark:text-gray-300 font-medium">
                        ${item.final_price}
                      </p>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {item.details}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => setPage(page > 1 ? page - 1 : 1)}
                disabled={page <= 1}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-400"
              >
                Previous
              </button>
              <span className="text-lg text-white font-medium">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}
                disabled={page >= totalPages}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <p className="text-lg text-gray-700 dark:text-gray-300">
            No results found for your search.
          </p>
        )}
      </div>
      <Footer />
    </>
  );
};

export default SearchResults;
